/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import dayjs from 'dayjs';
import { Observable, Subject } from 'rxjs';
import { MimeTypes } from '../engine/mime-types';
import { ProgressStatus } from '../enum/progress-status.enum';
import { CommonUtils } from '../tool/common-utils';
import { NameValue } from '../tool/name-value';
import { SelectableEntity } from './entity/selectable-entity';
import { FileType } from './file-type.enum';
import { GedRecord } from './ged-record';

/**
 * Representation of Files loaded by Wi Ged Protocol
 */
export class GedFile extends SelectableEntity {
  public status: ProgressStatus;
  private _file: File | GedRecord;
  public metadata: string;
  public encoded: string;
  public canRemove = true;
  public user: string;
  public fileType = FileType.file;
  public metaKeys: NameValue[] = [];
  public dds: NameValue[] = [];
  private _createdAt = 0;

  public get createdAt(): number {
    return this._createdAt;
  }
  public set createdAt(value: number) {
    this._createdAt = value;
  }
  public setCreatedAt(createdDate: string, createdTime: string): void {
    try {
      this._createdAt = dayjs(
        `${createdDate} ${createdTime}`,
        'DD/MM/YYYY HH:mm:ss',
      ).valueOf();
    } catch (error) {
      console.warn('Error setting created at date/time for ged file', error);
    }
  }

  private _endpoint = '';
  public get endpoint(): string {
    return this._endpoint;
  }
  public set endpoint(value: string) {
    this._endpoint = value.replaceAll('//', '/');
  }

  public base64 = '';
  public thumbnailBase64 = '';
  public position: number;

  public get file(): File {
    return this._file instanceof File ? <File>this._file : null;
  }

  constructor(file: File | GedRecord, user?: string) {
    super(CommonUtils.uuid(), '', 'label');
    this.status = ProgressStatus.waiting;
    this._file = file;
    if (file instanceof GedRecord) {
      this.user = file.user;
      this.status = ProgressStatus.send;
      this.metadata = file.metadata;
      this.encoded = this.getImageUrl();
      this.position = file.position;
      this.label = file.name;
    } else {
      this.user = user;
      this.readFile(file);
    }
    this.generateFileType();
  }
  get name(): string {
    return this._file.name;
  }

  get type(): string {
    return this._file.type;
  }

  get lastModifiedDate(): Date {
    return new Date(this._file.lastModified);
  }
  get lastModifiedDateTime(): string {
    return CommonUtils.convertDateToDateTimeString(this.lastModifiedDate);
  }
  getFile(): File {
    if (this._file instanceof File) {
      return <File>this._file;
    } else {
      return null;
    }
  }

  setFile(file: File) {
    this.readFile(file);
    this._file = file;
  }
  get send(): boolean {
    return this.status === ProgressStatus.send;
  }

  private getImageUrl(): string {
    const f = <GedRecord>this._file;
    if (f && f.type && f.type.indexOf('images') >= 0) {
      return f.metadata;
    } else {
      return null;
    }
  }

  private readFile(file: File) {
    if (file) {
      this.label = file.name;
      const reader = new FileReader();
      reader.onload = (event: any) => {
        this.encoded = event.target.result;
        this.base64 = event.target.result;
        this.thumbnailBase64 = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  override convert(item): any {
    void item;
    return undefined;
  }

  getApi(): string {
    return '';
  }

  override getId(): string {
    return this.metadata ? this.metadata : this.encoded;
  }

  override getLabel(): string {
    return this.name;
  }

  override serialize() {
    return null;
  }

  private generateFileType() {
    const name = this.name;
    const index = name.lastIndexOf('.');
    if (index < 0) {
      return;
    }
    const extension = this.name.slice(index + 1).toLocaleLowerCase();
    if (MimeTypes.images.indexOf(extension) >= 0) {
      this.icon = 'file-image';
      this.fileType = FileType.image;
      return;
    }
    switch (extension) {
      case 'doc':
      case 'docx':
        this.icon = 'file-word';
        this.fileType = FileType.word;
        return;
      case 'xls':
      case 'xlsx':
        this.icon = 'file-excel';
        this.fileType = FileType.excel;
        return;
      case 'ppt':
      case 'pptx':
        this.icon = 'file-powerpoint';
        this.fileType = FileType.powerpoint;
        return;
      case 'msg':
        this.icon = 'envelope';
        this.fileType = FileType.message;
        break;
      case 'csv':
        this.icon = 'file-csv';
        this.fileType = FileType.csv;
        return;
      case 'pdf':
        this.icon = 'file-pdf';
        this.fileType = FileType.pdf;
        return;
      case 'json':
        this.icon = 'file-brackets-curly';
        this.fileType = FileType.json;
        return;
    }
    if (MimeTypes.archives.indexOf(extension) >= 0) {
      this.icon = 'file-archive';
      this.fileType = FileType.archive;
      return;
    }
    this.icon = 'file';
    this.fileType = FileType.file;
  }

  getDds(): NameValue[] {
    if (this._file instanceof GedRecord) {
      return this._file.dds;
    }
    return this.dds;
  }

  public serializeToJson$(): Observable<unknown> {
    if (this.fileType !== FileType.json) {
      return new Observable<unknown>((subscriber) => {
        subscriber.error('File type is not JSON');
        subscriber.complete();
      });
    }
    const obs$ = new Subject<unknown>();
    const file: File = this.getFile();
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const content = e.target.result;
        const jsonData = JSON.parse(content);
        if (jsonData) {
          obs$.next(jsonData);
          obs$.complete();
        }
      } catch {
        obs$.error('Error reading file:');
      }
    };
    reader.readAsText(file);
    return obs$;
  }
}
